import { useMemo, useRef, useState } from 'react';

import { useUpdatePromoterMutation } from '@mutations/PromoterMutations';

import { useAnalytics } from '@hooks/useAnalytics';
import { useShareLink } from '@hooks/useShareLink';

import Button from '@components/buttons/Button';
import { Input } from '@components/form/Input';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';

interface WerqWithUsLinkProps {
  isCurrentPromoter: boolean;
  promoterId: string;
  bookingHandle: string;
}

const WerqWithUsLinkComponent = ({
  isCurrentPromoter,
  promoterId,
  bookingHandle: initialBookingHandle,
}: WerqWithUsLinkProps) => {
  const { track } = useAnalytics();
  const { didCopy, shareLink } = useShareLink();
  const [isEditing, setIsEditing] = useState(false);
  const [bookingHandle, setBookingHandle] = useState(initialBookingHandle);
  const inputRef = useRef<HTMLInputElement>(null);
  const { isSubmitting: isUpdatingPromoter, updatePromoterAsync } = useUpdatePromoterMutation();

  const bookingLinkRoot = 'werqwith.us/';
  const handleShareClick = async () => {
    if (isEditing) {
      track('Save booking handle');
      await updatePromoterAsync({
        promoterId,
        bookingHandle,
      });
      setIsEditing(false);
    }

    track('Click share entertainer');
    if (isCurrentPromoter) {
      await updatePromoterAsync({
        promoterId,
        linkCopiedAt: new Date(),
      });
    }
    await shareLink({
      url: `https://${bookingLinkRoot}${bookingHandle}`,
    });
  };

  const handleEditClick = () => {
    setIsEditing(true);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  };

  const editButtonText = useMemo(() => {
    if (isUpdatingPromoter) {
      return 'Saving...';
    }

    if (didCopy) {
      return 'Copied!';
    }

    return 'Save';
  }, [isUpdatingPromoter, didCopy]);

  if (isEditing) {
    return (
      <div
        className="d-flex align-items-center justify-content-between ps-2"
        style={{
          border: '1px solid rgba(255,255,255,.2)',
          borderRadius: 8,
        }}
      >
        <div className="d-flex align-items-center gap-2">
          <div>
            <FontAwesomeIcon name="link" />
          </div>

          <div className="d-flex align-items-center">
            <span>{bookingLinkRoot}</span>
            <Input
              ref={inputRef}
              className="ps-0 py-1"
              wrapperClassName="flex-grow-1"
              defaultValue={bookingHandle}
              onChange={(e) => setBookingHandle(e.target.value)}
              style={{ height: 24, fontSize: 14, background: 'transparent' }}
            />
          </div>
        </div>
        <div>
          <Button
            variant="flat"
            roundness="rounded"
            size="sm"
            color="neutral"
            className="py-2 px-2 opacity-90"
            onClick={handleShareClick}
          >
            {editButtonText}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="d-flex align-items-center justify-content-between ps-2"
      style={{
        border: '1px solid rgba(255,255,255,.2)',
        borderRadius: 8,
      }}
    >
      <div className="d-flex align-items-center gap-2">
        {isCurrentPromoter ? (
          <div onClick={handleEditClick}>
            <FontAwesomeIcon name="edit" faStyle="far" />
          </div>
        ) : (
          <div>
            <FontAwesomeIcon name="link" />
          </div>
        )}

        <div>
          {bookingLinkRoot}
          {bookingHandle}
        </div>
      </div>
      <div>
        <Button
          variant="flat"
          roundness="rounded"
          size="sm"
          color="neutral"
          startIcon={
            didCopy ? <FontAwesomeIcon name="check" /> : <FontAwesomeIcon name="share-square" />
          }
          className="py-2 px-2 opacity-90"
          onClick={handleShareClick}
        >
          {didCopy ? 'Copied!' : 'Share'}
        </Button>
      </div>
    </div>
  );
};

export const WerqWithUsLink = (props: WerqWithUsLinkProps) => (
  <WerqWithUsLinkComponent key={`werq-with-us-link-${props.promoterId}`} {...props} />
);
